import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { FiligreeFrame } from "@/components/brand/FiligreeFrame";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { seo } from "@/lib/seo";
import { isLevel } from "@/config/compliance";
import { ConsultCTA } from "@/components/brand/ConsultCTA";

export const Route = createFileRoute("/family-capital-system")({
  head: () =>
    seo({
      title:
        "The Family Capital System — Control, Liquidity, Continuity | Currency Thoughts",
      description:
        "A product-neutral guide to the three principles behind generational wealth: control, liquidity, and continuity. Veteran-led family financial education.",
      canonical: "/family-capital-system",
    }),
  component: FamilyCapitalSystemPage,
});

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2
      className="mt-16 font-display font-semibold text-navy-800"
      style={{ fontSize: "30px", lineHeight: 1.2, letterSpacing: "-0.01em" }}
    >
      {children}
    </h2>
  );
}

function P({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="mt-5 font-sans text-charcoal-900/85"
      style={{ fontSize: "16px", lineHeight: 1.7 }}
    >
      {children}
    </p>
  );
}

function FamilyCapitalSystemPage() {
  return (
    <div className="flex min-h-screen flex-col bg-cream-50">
      <Navbar />
      <main className="flex-1">
        <section className="bg-navy-900 text-cream-50">
          <FiligreeFrame intensity="subtle">
            <div className="mx-auto max-w-4xl px-6 pt-32 pb-20 text-center">
              <Eyebrow color="bronze" align="center">
                Family Financial Education
              </Eyebrow>
              <h1
                className="mt-8 font-display font-bold text-cream-50"
                style={{
                  fontSize: "clamp(2.25rem, 5vw, 3.5rem)",
                  lineHeight: 1.15,
                  letterSpacing: "-0.01em",
                }}
              >
                The Family Capital System
              </h1>
              <p
                className="mx-auto mt-8 font-sans text-cream-50/85"
                style={{ fontSize: "17px", lineHeight: 1.7, maxWidth: "640px" }}
              >
                Three principles — Control, Liquidity, and Continuity — and why most families
                only ever get taught the first one.
              </p>
            </div>
          </FiligreeFrame>
        </section>

        <article className="mx-auto max-w-3xl px-6 py-16">
          <P>
            Most household money education stops at a single idea: buy protection, hold it, and
            hope you never need it. That is one third of a system. It is a necessary third, and
            it is the part the industry is best at explaining, but a family that only learns
            that third ends up with a stack of paperwork instead of a plan. The paperwork is not
            the plan. The plan is the set of decisions that determine who controls the money,
            how fast that money can move when your family needs it, and what happens to it when
            the people who built it are no longer here to direct it.
          </P>
          <P>
            We call that set of decisions the Family Capital System. It is not a product, it is
            not a package, and it is not something you buy. It is a way of organizing three
            questions that every household eventually has to answer, whether they answer them
            deliberately or by default. Families who answer them deliberately tend to keep what
            they build. Families who answer them by default tend to watch it dissipate — often
            within two generations, almost always within three.
          </P>
          <P>
            This page is educational and product-neutral. Nothing here is a recommendation to
            purchase any specific product, and nothing here is legal, tax, or investment advice.
            Our purpose is to give you the vocabulary and the mechanics so that when someone
            eventually does show you a product, you can evaluate it yourself.
          </P>

          <H2>Principle One — Control</H2>
          <P>
            Control is the question of who actually decides what happens to a dollar. It sounds
            obvious until you audit your own balance sheet honestly. Money inside an employer
            plan is subject to the plan's rules, the employer's continuation, and a penalty
            structure that governs when you may touch it. Money inside home equity is subject to
            a lender's willingness to underwrite you at the moment you need it — which is
            frequently the exact moment they are least willing. Money inside a business is
            subject to the business surviving. Wealth you don't control isn't wealth. It's
            exposure.
          </P>
          <P>
            Control has two dimensions worth separating. The first is legal control: whose name
            is on the asset, who is the owner of record, who can direct it, and what contract or
            statute governs that direction. The second is practical control: whether you can
            actually access the value on your own timeline without permission, penalty, or a
            forced sale at a bad price. An asset can be legally yours and practically frozen.
            Most families discover this distinction during an emergency, which is the worst
            possible classroom.
          </P>
          <P>
            Protection structure is a control question before it is a coverage question. The
            common framing is "how much coverage do I need," and that framing is incomplete. The
            better questions are who owns the coverage, who is named to receive it, whether the
            structure survives a change in employment, and whether the household could absorb a
            job loss, a disabling illness, or a death without unwinding a decade of progress.
            When protection is structured correctly, a bad year is a bad year. When it is
            structured poorly, a bad year is a permanent reset.
          </P>
          <P>
            A practical control exercise: list every asset your household holds, and next to
            each one write down who must say yes before you can use it. If the answer is
            anything other than "we do," you have found a control gap. Control gaps are not
            automatically bad — some of them are trades you consciously made for a tax treatment
            or an employer match — but they should be conscious trades, not surprises.
          </P>

          <H2>Principle Two — Liquidity</H2>
          <P>
            Liquidity is the question of how fast capital can move without destroying itself.
            Money locked away until 59½ can't help you at 41. The typical household accumulation
            plan is optimized entirely for a single date decades away, which is a defensible
            goal and a poor system, because life does not schedule its capital needs around
            retirement age. Opportunities, emergencies, tuition, a business, a relocation, a
            parent who needs care — these arrive on their own timetable.
          </P>
          <P>
            The velocity of money is the idea that the same dollar can do more than one job over
            time if it is positioned where it remains accessible. When a dollar is spent, its
            work is finished. When a dollar is parked somewhere illiquid, its work is deferred.
            When a dollar sits in a position that continues to accumulate while remaining
            accessible through a contractual provision, it can continue working while you also
            use its value. That is the mechanical claim, and it is worth understanding precisely
            rather than romantically.
          </P>
          <P>
            Cash value life insurance is one instrument families encounter in this conversation.
            The mechanics worth understanding are these. Certain permanent policies accumulate a
            cash value over time. Certain of those policies include a contractual policy loan
            provision, meaning the policyholder may request a loan from the carrier using the
            policy as collateral, under terms the contract specifies. A policy loan is a loan:
            it carries an interest rate set by the contract, it reduces the death benefit while
            outstanding, and if it is not managed it can affect the policy's ability to stay in
            force. The specific accumulation behavior, loan rate, loan availability, and effect
            on the death benefit vary entirely by product, carrier, and state.
          </P>
          <P>
            You may have heard these liquidity principles taught under the name Infinite Banking.
            The Infinite Banking Concept® is a strategy developed by R. Nelson Nash and is a
            registered trademark of Infinite Banking Concepts, LLC. Currency Thoughts is not
            affiliated with, endorsed by, or certified by Infinite Banking Concepts, LLC or the
            Nelson Nash Institute. We teach the underlying financial principles — cash value
            accumulation, policy loan mechanics, and the velocity of money — on a product-neutral
            basis. The specific features, loan provisions, and results of any policy are governed
            entirely by the contract issued by the insurance carrier and vary by product,
            carrier, and state.
          </P>
          <P>
            The reason we teach liquidity as its own principle, rather than folding it into a
            product conversation, is that the principle stands on its own. A household emergency
            reserve is a liquidity decision. A line of credit arranged before you need it is a
            liquidity decision. The order in which you draw down different accounts is a
            liquidity decision. Cash value liquidity is one option among several, and it should
            be evaluated against the others on its actual contractual terms, not on a story.
          </P>

          <H2>Principle Three — Continuity</H2>
          <P>
            Continuity is the question of what survives you. A policy is not a plan. A policy is
            an instrument that pays a stated amount to a stated party under stated conditions.
            Whether that payment accomplishes anything for your family depends on structure that
            sits outside the policy: who owns it, who is named, whether a trust is involved, and
            whether the rest of the estate is organized to match.
          </P>
          <P>
            Three mechanics do most of the work here. Ownership determines whose estate an asset
            belongs to and can affect how it is taxed and whether creditors can reach it.
            Beneficiary designation is a direct contractual instruction to the carrier or
            custodian, and it generally controls regardless of what a will says — which is why
            an outdated designation naming a former spouse is one of the most common and most
            damaging errors we see. Trust structure allows you to attach conditions, timing, and
            stewardship to a transfer instead of handing a lump sum to a beneficiary with no
            framework for it.
          </P>
          <P>
            The failure mode is leakage. Assets that pass through probate are slower, public, and
            more expensive. Assets that transfer to an unprepared recipient often do not survive
            the recipient. Assets that transfer without tax planning may transfer smaller than
            intended. Each of these is a structural problem with a structural answer, and none of
            them is solved by buying more coverage.
          </P>
          <P>
            Continuity also includes the non-financial transfer: the documents your family can
            actually find, the passwords, the account list, the letter explaining why you set it
            up the way you did. A family that inherits money without inheriting the reasoning
            behind it tends to dismantle the structure within a few years.
          </P>

          <H2>How the three principles work together</H2>
          <P>
            The principles are sequential, and skipping ahead is the most common mistake. Control
            comes first because a household that cannot direct its own capital cannot execute any
            further strategy. Liquidity comes second because a household with control but no
            accessible capital will be forced to dismantle its structure at the first emergency.
            Continuity comes last because there is nothing to transfer until the first two are
            handled — and because continuity structures assume the underlying assets stay intact
            long enough to reach the next generation.
          </P>
          <P>
            Run in order, they compound. Control lets you decide. Liquidity lets you act on the
            decision. Continuity lets the result outlive you. Run out of order — a sophisticated
            trust over an underfunded, illiquid, poorly controlled balance sheet — and the
            structure is decorative.
          </P>

          <H2>Common misconceptions</H2>
          <P>
            <strong>"There's a product that lets you be your own bank."</strong> There is no such
            thing as a policy that lets you "be your own bank." That phrase is a teaching
            metaphor for using the contractual policy loan provisions inside a permanent life
            insurance contract. It is not a banking product. It is not a deposit account. It is
            not FDIC insured. No policy makes you a bank, no policy creates money, and every
            dollar of liquidity comes from a contract with a carrier on that carrier's terms.
          </P>
          <P>
            <strong>"Policy loans are free money."</strong> They are loans. They accrue interest
            at the rate the contract specifies, they reduce the death benefit while outstanding,
            and mismanaged loans can jeopardize the policy. Understanding the loan provision in
            the actual contract is the entire exercise.
          </P>
          <P>
            <strong>"An illustration is a projection of what will happen."</strong> An
            illustration is a hypothetical model built on assumptions. Non-guaranteed elements
            are, by definition, not guaranteed. Read the guaranteed column.
          </P>
          <P>
            <strong>"My will handles everything."</strong> Beneficiary designations on insurance
            and retirement accounts generally override a will. Reviewing designations is usually
            faster, cheaper, and more consequential than rewriting a will.
          </P>
          <P>
            <strong>"This is only for wealthy families."</strong> Control, liquidity, and
            continuity are structural questions, not balance-sheet-size questions. A household
            with modest assets and good structure frequently outperforms a household with larger
            assets and none.
          </P>

          <H2>Where to start</H2>
          <P>
            Start with an inventory, not a purchase. Write down every asset, every debt, every
            protection policy in force, and every beneficiary designation currently on file.
            Confirm each designation is current. Note, for each asset, who must approve before
            you can access it and what it would cost you to access it this month. That single
            document usually reveals the household's real gaps faster than any calculator.
          </P>
          <P>
            Then read. Our Family Library holds product-neutral explainers on each of these three
            principles, written for households rather than for the industry. Learn the mechanics
            before anyone shows you a product, and you will be able to evaluate whatever you are
            eventually shown.
          </P>

          <div className="mt-14 border-t border-bronze-600/40 pt-10 text-center">
            {isLevel("education_only") && (
              <PrimaryButton size="lg" asChild>
                <Link to="/academy/library">Read the Family Library</Link>
              </PrimaryButton>
            )}
            {!isLevel("education_only") && <ConsultCTA />}

          </div>

          <p
            className="mt-10 font-sans text-charcoal-500"
            style={{ fontSize: "12px", lineHeight: 1.7 }}
          >
            This page is general financial education. It is not legal, tax, or investment advice
            and is not a recommendation to purchase any specific product. All policy features and
            benefits are governed by the contract issued by the insurance carrier and vary by
            product, carrier, and state.
          </p>
        </article>
      </main>
      <Footer />
    </div>
  );
}
